import type { CustomerContact } from "@/Domain/CustomerContact";
import type { API } from ".";
import { EnumStatementType } from "../Domain/EnumStatementType";
import type { AddOrModifyCustomerContactRequest } from "@/Models/Requests/AddOrModifyCustomerContactRequest";

export type GetCustomerContactsResponse = {
  TotalItemsInList: number;
  ListItems: CustomerContact[];
};

export type GetCustomerContactsRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  sortFields: string;
  sortDirs: string;
  isCompanyContact: boolean;
  id: string | null;
  allContacts: boolean;
  distinct: boolean;
  fields: string;
  operators: string;
  values: string;
  skip: number;
  take: number;
};

export type DeliveryMethod = "N/A" | "Paper" | "Email" | "Both";

export type AddOrModifyCustomerContactsRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  gridId: string;
  plantId: string;
  isCompanyContact: boolean;
  ccOrPcId: number;
  contactId: number;
  isPrimary: boolean;
  isBilling: boolean;
  isCopacker: boolean;
  isWeb: boolean;
  isOther: boolean;
  modifiedBy: string | undefined;
  invoiceDeliveryMethod: DeliveryMethod;
  statementDeliveryMethod: DeliveryMethod;
  scheduleAEmail: boolean;
  scheduleBEmail: boolean;
  eirEmail: boolean;
};

export type DeleteCustomerContactRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  gridId: string;
  isCompanyContact: boolean;
  ccOrPcId: number;
};

export function CreateAddOrModifyCustomerContactRequest(
  theUserLogin: string | undefined,
  theCompanyLogin: string | undefined,
  isAdd: boolean,
  facilityId: string,
  facilityName: string,
  contactId: number,
  jobTitle: string,
  customerContact: CustomerContact | undefined
): API["AddOrModifyCustomerContact"]["request"] {
  return {
    CompanyLogin: theCompanyLogin,
    GridId: "customerContacts", // this is just a dummy value. It is returned in the response, but is not used in this Vue.js version of contacts.
    PlantId: isAdd ? Number(facilityId) : -1,
    IsCompanyContact: facilityName.substr(0, 11) == "Main Office",
    CcOrPcId: isAdd || !customerContact ? 0 : customerContact.id,
    ContactId: contactId,
    IsPrimary:
      isAdd || !customerContact ? false : customerContact.primaryCT ?? false,
    IsBilling:
      isAdd || !customerContact ? false : customerContact.billingCT ?? false,
    IsWeb: isAdd || !customerContact ? false : customerContact.webCT ?? false,
    IsCopacker:
      isAdd || !customerContact ? false : customerContact.copackerCT ?? false,
    IsOther:
      isAdd || !customerContact ? false : customerContact.otherCT ?? false,
    ModifiedBy: theUserLogin,
    InvoiceDeliveryMethod:
      isAdd || !customerContact || customerContact.InvoiceDeliveryMethod == null
        ? "N/A"
        : (EnumStatementType[
            customerContact.InvoiceDeliveryMethod
          ] as DeliveryMethod) || "N/A",
    StatementDeliveryMethod:
      isAdd || !customerContact || customerContact.StatementDeliveryMethod == null
        ? "N/A"
        : (EnumStatementType[
            customerContact.StatementDeliveryMethod
          ] as DeliveryMethod) || "N/A",
    ScheduleAEmail:
      isAdd || !customerContact
        ? false
        : customerContact.ScheduleAEmail ?? false,
    ScheduleBEmail:
      isAdd || !customerContact
        ? false
        : customerContact.ScheduleBEmail ?? false,
    EirEmail: !customerContact ? false : customerContact.EIREmail ?? false,
    CompanyTitle: jobTitle,
  } as AddOrModifyCustomerContactRequest;
}

export function CreateDeleteCustomerContactRequest(
  theUserLogin: string | undefined,
  theCompanyLogin: string | undefined,
  facility: string,
  id: number
): API["DeleteCustomerContact"]["request"] {
  const isCompanyContact = !!facility.match("Main Office");

  return {
    userLogin: theUserLogin,
    companyLogin: theCompanyLogin,
    gridId: "customerContacts",
    isCompanyContact,
    ccOrPcId: id,
  } as DeleteCustomerContactRequest;
}
